import { DragEndEvent } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { ObjectID } from "bson";
import React, { useRef } from "react";

import { faExclamationCircle, faGripLines, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faXmarkCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useKeyPress } from "@performyard/py-components";
import {
	ArrayFieldState,
	ButtonStandard,
	FormField,
	FormInputText,
	FormList,
	Icon,
	InputNumeric,
	Text,
} from "@performyard/py-components";
import { RatingScaleOptionJson, SurveyQuestionJson } from "@performyard/service-contracts";

//components
import IconButton from "../../../../../../../../../components/Button/IconButton";
import DragAndDrop from "../../../../../../../../../components/DragAndDrop";
import Spacer from "../../../../../../../../../components/Spacer";

import { useInvalidQuestionMessages } from "../../../store/store";

import { useCompanySettings } from "../../../../../../../../../api/hooks";

type RatingScaleRowProps = {
	index: number;
	parentIndex: number;
	field: ArrayFieldState<RatingScaleOptionJson>;
};

const SurveyBuilderRatingScaleNumericInput = ({
	id,
	value,
	onChange,
	precision,
	placeholder,
}: {
	id: string;
	value: number | "";
	onChange: (value: number | "") => void;
	precision: number;
	placeholder: string;
}) => {
	return (
		<InputNumeric
			id={id}
			value={value === "" || value === undefined ? null : value}
			onChange={(value) => onChange(value === null ? "" : value)}
			precision={precision}
			placeholder={placeholder}
			allowEmpty
		/>
	);
};

export const RatingScaleRow = ({ index, field, parentIndex }: RatingScaleRowProps) => {
	const { data: settings } = useCompanySettings();
	const { attributes, listeners, setNodeRef, transform } = useSortable({ id: `${index}` });

	// store
	const errors = useInvalidQuestionMessages();

	const textError = errors?.options?.[index.toString()]?.text ? errors.options[index.toString()].text : undefined;
	const descriptionError = errors?.options?.[index.toString()]?.description
		? errors.options[index.toString()].description
		: undefined;

	const style = {
		transform: CSS.Translate.toString(transform),
	};

	const decimalPlaces = settings?.decimal_places || 0;

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			className="rating-scale-row flex gap-2"
			data-testid="rating-scale-row"
		>
			<div
				{...listeners}
				className="flex h-8 w-6 flex-shrink-0 cursor-grab items-center justify-center border-r border-neutral-2"
			>
				<Icon color="neutral-7" icon={faGripLines} dangerouslySetClassName="hover:text-neutral-9" />
			</div>
			<div className="w-[88px]">
				<FormField<number | "">
					id={`questions[${parentIndex}].options[${index}].value`}
					input={(field) => {
						return (
							<SurveyBuilderRatingScaleNumericInput
								id={`questions[${parentIndex}].options[${index}].value`}
								value={field.value}
								onChange={field.onChange}
								precision={decimalPlaces}
								placeholder="Value"
							/>
						);
					}}
				/>
			</div>
			<div className="max-w-[288.5px] flex-grow">
				<FormInputText id={`questions[${parentIndex}].options[${index}].text`} placeholder="Name" />
				{textError ? (
					<div className="flex items-center gap-1 pt-2 text-paragraph-3 font-semibold text-red-6">
						<FontAwesomeIcon icon={faExclamationCircle} />
						{textError}
					</div>
				) : null}
			</div>
			<div className="max-w-[288.5px] flex-grow">
				<FormInputText
					id={`questions[${parentIndex}].options[${index}].description`}
					placeholder="Description"
				/>
				{descriptionError ? (
					<div className="flex w-full items-center gap-1 pt-2 text-paragraph-3 font-semibold text-red-6">
						<FontAwesomeIcon icon={faExclamationCircle} />
						{descriptionError}
					</div>
				) : null}
			</div>
			{field?.value?.length > 1 && (
				<div className="flex h-8 items-center justify-center">
					<IconButton
						icon={faXmarkCircle}
						color="gray"
						onClick={() => field.removeValue(index)}
						aria-label="Delete option"
						style={{ padding: 0, width: 20 }}
						testId="delete-option"
					/>
				</div>
			)}
		</div>
	);
};

const RatingScaleEntries = ({ field, index }: { field: ArrayFieldState<RatingScaleOptionJson>; index: number }) => {
	const { value, pushValue } = field;
	const [ref] = useAutoAnimate();
	const containerRef = useRef<HTMLDivElement>(null);

	const MAX_RATING_SCALE_OPTIONS = 20;

	const handleAddOption = () => {
		if (value?.length >= MAX_RATING_SCALE_OPTIONS) {
			return;
		}

		const newOption: RatingScaleOptionJson = {
			value: "",
			description: "",
			_id: new ObjectID().toString(),
			text: "",
			order: value.length + 1,
		};

		pushValue(newOption);

		// wait 10 ms and then focus on the last input to wait for render
		setTimeout(() => {
			const lastInput = document.getElementById(`questions[${index}].options[${value?.length}].value`);
			if (lastInput) {
				lastInput.focus();
			}
		}, 10);
	};

	useKeyPress({
		key: "Enter",
		onPress: (e) => {
			if (e.target instanceof HTMLInputElement && value?.length < MAX_RATING_SCALE_OPTIONS) {
				e.preventDefault();
				e.stopPropagation();
				handleAddOption();

				// wait 10 ms and then focus on the last input to wait for render
				setTimeout(() => {
					const lastInput = document.getElementById(`questions[${index}].options[${value?.length}].value`);
					if (lastInput) {
						lastInput.focus();
					}
				}, 10);
			}
		},
		containerRef,
	});

	return (
		<div ref={containerRef}>
			{value?.length > 0 && (
				<DragAndDrop
					itemsContainerRef={ref}
					restrictToParentElement
					restrictToVerticalAxis
					itemsContainerClassName="flex flex-col gap-2"
					handleOnDragEnd={(event: DragEndEvent) => {
						const { active, over } = event;

						if (active.id !== over?.id) {
							const oldIndex = Number(active.id);
							const newIndex = Number(over?.id);
							field.shiftValue(oldIndex, newIndex);
						}
					}}
					itemIds={value.map((_choice, index) => index.toString())}
					draggableNodeItems={value.map((_choice, listIndex) => (
						<RatingScaleRow key={`${listIndex}`} index={listIndex} parentIndex={index} field={field} />
					))}
				/>
			)}
			{value?.length >= MAX_RATING_SCALE_OPTIONS && (
				<div className="flex items-center gap-2 pt-2">
					<Icon size={16} color="red-6" icon={faExclamationCircle} />
					<Text size="caption" weight="semi-bold" color="red-6">
						You have reached the maximum number of options
					</Text>
				</div>
			)}
			<Spacer height={2} />
			<div className="flex w-full justify-end">
				<ButtonStandard
					id={`add-option-${index}`}
					iconLeft={faPlus}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						if (value?.length >= MAX_RATING_SCALE_OPTIONS) {
							return;
						}
						handleAddOption();
					}}
					disabled={value?.length >= MAX_RATING_SCALE_OPTIONS}
				>
					Add Option
				</ButtonStandard>
			</div>
		</div>
	);
};

const RatingScaleEdit = ({ index }: { index: number; mutateField: (value: SurveyQuestionJson) => void }) => {
	return (
		<div>
			<Text overrideElement="h6" size="paragraph-3" weight="semi-bold">
				Rating Scale Options
			</Text>
			<Spacer height={1} />
			<FormList<RatingScaleOptionJson>
				id={`questions[${index}].options`}
				input={(field) => <RatingScaleEntries field={field} index={index} />}
			/>
		</div>
	);
};

export default RatingScaleEdit;
